/** Copyright (c) 2017 T. Rowe Price. All rights reserved. */

import { cloneDeep, isArray, isEqual, isEmpty, keyBy, mapValues } from 'lodash';

import { aggregateGrowth, aggregateMetrics } from 'services/Calculation';
import { accountName, formatAccountNumber } from 'services/Formats';
import {
  CHANGE_IRA_CONTRIBUTION_TOGGLE,
  RECEIVED_MAILING_ADDRESS,
  RECEIVED_RESIDENTIAL_ADDRESS,
  RECEIVED_INTRADAY_PRICING_MANUAL,
  MOBILE_SELECTED_ACCOUNT_FILTER,
  MOBILE_SELECTED_PERIOD,
  MOBILE_TOGGLED_PRODUCT,
  MOBILE_TOGGLED_PURPOSE,
  RECEIVED_ACCOUNT_GROWTH,
  RECEIVED_INDIVIDUAL_ACCOUNT_GROWTH,
  RECEIVED_CONSOLIDATED_PERFORMANCE,
  RECEIVED_HISTORY,
  RECEIVED_PRODUCT_PERFORMANCE,
  SELECTED_ACCOUNT_FILTER,
  // SELECTED_MANAGEMENT_FILTER,
  SET_MOBILE_FILTERS,
  TOGGLED_ALL_PRODUCTS,
  TOGGLED_ALL_PURPOSES,
  TOGGLED_PRODUCT,
  RECEIVED_AGGREGATED_PRODUCT_PERFORMANCE,
  RECEIVED_INTRADAY_PRICING_AUTOMATIC,
  RECEIVED_CDS_PORTFOLIO,
  UPDATE_INTRADAY_ACCOUNTS,
  RECEIVED_UNREALIZED_GAINS_LOSSES,
  RECEIVED_REALIZED_GAINS_LOSSES_PRIOR,
  RECEIVED_REALIZED_GAINS_LOSSES_CURRENT,
  IS_PRINT_MODE
  // TOGGLED_PURPOSE
} from './Actions';
import { RECEIVED_ACCOUNTS } from './account/AccountBalancesActions';
import {
  HIDE_SHOW_CHANGED,
  HIDE_SHOW_ALL,
  RESET_ACCOUNT_VISIBILITY,
  ERROR_ACCOUNT_VISIBILITY
} from 'state/preferences/PreferencesActions';
import { RECEIVED_ACCOUNT_PERFORMANCE } from './account/AccountPerformanceActions';
import { PERSONAL_INVESTMENT } from './performance/ProductPerformanceSelectors';
import { getUpdatedReceivedAccounts } from './account/AccountSelectors';

const noop = (state) => state || {};

export const accountFilters = noop;

export const accounts = (state = [], action, fullState) => {
  if (action.type === RECEIVED_ACCOUNTS) {
    return action.accounts;
  }

  if (action.type === HIDE_SHOW_CHANGED) {
    const { payload } = action;
    return state.map((account) => {
      let newAccount = cloneDeep(account);
      if (newAccount.accountId === payload.accountDetails.name.accountId) {
        newAccount.hiddenAccount = payload.accountDetails.hiddenAccount;
      }
      return newAccount;
    });
  }

  if (action.type === HIDE_SHOW_ALL) {
    return state.map((account) => {
      let newAccount = cloneDeep(account);
      newAccount.hiddenAccount = !action.hideOrShow;
      return newAccount;
    });
  }

  if (
    action.type === RESET_ACCOUNT_VISIBILITY ||
    action.type === ERROR_ACCOUNT_VISIBILITY
  ) {
    return getUpdatedReceivedAccounts(fullState);
  }

  return state;
};
accounts.priority = 10;

export const initialReceivedAccounts = (state = [], action) => {
  if (action.type === RECEIVED_ACCOUNTS) {
    return action.accounts;
  }
  return state;
};

export const visibleAccounts = (state = [], action) => {
  if (action.type === RECEIVED_ACCOUNTS) {
    return action.accounts.filter((account) => !account.hiddenAccount);
  }
  return state;
};
visibleAccounts.priority = 11;

export const clientProductTypes = (state = {}, action) => {
  if (action.type !== RECEIVED_ACCOUNTS || !Array.isArray(action.accounts)) {
    return state;
  }

  const hasTypes = {};

  action.accounts.forEach((account) => (hasTypes[account.productType] = true));

  return hasTypes;
};
clientProductTypes.priority = 12;

export const accountsById = (state = {}, action) => {
  if (action.type !== RECEIVED_ACCOUNTS) {
    return state;
  }

  return keyBy(action.accounts, 'accountId');
};
accountsById.priority = 13;

export const visibleAccountsById = (state = {}, action, fullState) => {
  if (action.type === RECEIVED_ACCOUNTS) {
    const { visibleAccounts } = fullState;
    return keyBy(visibleAccounts, 'accountId');
  }
  return state;
};
visibleAccountsById.priority = 13;

export const products = (state = {}, action) => {
  if (action.type !== RECEIVED_ACCOUNTS) {
    return state;
  }
  const products = cloneDeep(state);

  for (let key in products.types) {
    products.types[key].userAccountNumbers = [];
  }

  action.accounts.forEach((obj) => {
    let product = products.byAccount[obj.productType];
    if (product) {
      product.userAccountNumbers.push(obj.accountNumber);
    }
  });

  return products;
};
products.priority = 14;

export const purposes = (state = {}, action) => {
  if (action.type === RECEIVED_ACCOUNTS) {
    let purposes = cloneDeep(state);
    action.accounts.forEach((obj) => {
      const purpose = obj.retirementAccount
        ? purposes.retirement
        : purposes.nonRetirement;
      purpose.userAccountNumbers.push(obj.accountNumber);
    });
    return purposes;
  }
  return state;
};
purposes.priority = 15;

export const intradayInitialState = {
  accounts: []
};

export const intraday = (state = intradayInitialState, action) => {
  if (
    action.type === RECEIVED_INTRADAY_PRICING_MANUAL ||
    action.type === RECEIVED_INTRADAY_PRICING_AUTOMATIC
  ) {
    return { accounts: action.accounts };
  }
  if (action.type === UPDATE_INTRADAY_ACCOUNTS) {
    const { payloadData } = action.payload;
    const sourceAccountDescriptor =
      payloadData?.distributionOptions[0]?.sourceAccountDescriptor;
    const cusIpId = sourceAccountDescriptor?.cusipId;
    return {
      accounts: state.accounts.map((account) => {
        const updatedPositions = sourceAccountDescriptor?.accountId === account.accountId
          ? account.positions.map((acc) => {
            if (acc.cusip === cusIpId) {
              return {
                ...acc,
                brokerageDivCapGains: {
                  ...acc.brokerageDivCapGains,
                  distributionOptions: payloadData.distributionOptions
                },
                recordStatus: 'PENDING'
              };
            }
            return acc;
          })
          : account.positions;
        return {
          ...account,
          positions: updatedPositions
        };
      })
    };
  }
  return state;
};

export const cdsPortfolio = (state = {}, action) => {
  if (action.type === RECEIVED_CDS_PORTFOLIO) {
    return action;
  }
  return state;
};

export const isPrintMode = (state = [], action) => {
  if (action.type === IS_PRINT_MODE) {
    return action;
  }
  return state;
};

export const unrealizedGainsLosses = (state = [], action) => {
  if (action.type === RECEIVED_UNREALIZED_GAINS_LOSSES) {
    return action;
  }
  return state;
};

export const realizedGainsLossesPrior = (state = [], action) => {
    if (action.type === RECEIVED_REALIZED_GAINS_LOSSES_PRIOR) {
        return action;
    }
    return state;
};

export const realizedGainsLossesCurrent = (state = [], action) => {
    if (action.type === RECEIVED_REALIZED_GAINS_LOSSES_CURRENT) {
        return action;
    }
    return state;
};

export const mobileFilterSelections = (state = {}, action, fullState) => {
  const mobileFilterSelections = cloneDeep(state);
  const purposeTypes = cloneDeep(fullState.purposes);
  const productTypes = cloneDeep(fullState.products);

  const { purposes, products } = mobileFilterSelections;

  /* User added or removed a product to the filter */
  if (action.type === MOBILE_TOGGLED_PRODUCT) {
    /* If all products were selected and user clicks one to be removed, loop through the user's
     available products and mark them as true, then mark the selected product as false.
     'products.all' will then evaulate to false */
    if (products.all && !products.subset[action.product]) {
      products.subset = mapValues(
        products.subset,
        (val, key) =>
          key !== action.product &&
          productTypes.types[key].userAccountNumbers &&
          productTypes.types[key].userAccountNumbers.length > 0
      );
    } else {
      products.subset[action.product] = !products.subset[action.product];
    }
    /* 'products.all' will evaluate to true if all of the user's available products are set to true,
     OR if all of the user's available products are set to false. The user must have at least one
     product selected, so we force set to all
     */
    products.all =
      Object.keys(products.subset)
        .filter(
          (a) =>
            productTypes.types[a].userAccountNumbers &&
            productTypes.types[a].userAccountNumbers.length > 0
        )
        .every((k) => products.subset[k]) ||
      Object.keys(products.subset)
        .filter(
          (a) =>
            productTypes.types[a].userAccountNumbers &&
            productTypes.types[a].userAccountNumbers.length > 0
        )
        .every((k) => !products.subset[k]);
  } else if (action.type === MOBILE_SELECTED_ACCOUNT_FILTER) {
    mobileFilterSelections.accountFilter = action.accountFilter;
  } else if (action.type === MOBILE_TOGGLED_PURPOSE) {
    /* User added or removed a purpose to the filter */
    /* If all purposes were selected and user clicks one to be removed, loop through the user's
     available purposes and mark them as true, then mark the selected purpose as false.
     'purposes.all' will then evaulate to false */
    if (purposes.all && !purposes.subset[action.purpose]) {
      purposes.subset = mapValues(
        purposes.subset,
        (val, key) =>
          key !== action.purpose &&
          purposeTypes[key].userAccountNumbers &&
          purposeTypes[key].userAccountNumbers.length > 0
      );
    } else {
      purposes.subset[action.purpose] = !purposes.subset[action.purpose];
    }
    /* 'purposes.all' will evaluate to true if all of the user's available purposes are set to true,
     OR if all of the user's available purposes are set to false. The user must have at least one
     purpose selected, so we force set to all
     */
    purposes.all =
      Object.keys(purposes.subset)
        .filter(
          (a) =>
            purposeTypes[a].userAccountNumbers &&
            purposeTypes[a].userAccountNumbers.length > 0
        )
        .every((k) => purposes.subset[k]) ||
      Object.keys(purposes.subset)
        .filter(
          (a) =>
            purposeTypes[a].userAccountNumbers &&
            purposeTypes[a].userAccountNumbers.length > 0
        )
        .every((k) => !purposes.subset[k]);
  } else if (action.type === MOBILE_SELECTED_PERIOD) {
    mobileFilterSelections.period = action.period;
  } else if (action.type === SET_MOBILE_FILTERS) {
    mobileFilterSelections.accountFilter = fullState.selections.accountFilter;
    mobileFilterSelections.products = cloneDeep(fullState.selections.products);
    mobileFilterSelections.purposes = cloneDeep(fullState.selections.purposes);
    mobileFilterSelections.period = fullState.selections.period;
  }

  return isEqual(mobileFilterSelections, state)
    ? state
    : mobileFilterSelections;
};
mobileFilterSelections.priority = 30;

export const accountPerformanceAggregate = (state = {}, action) => {
  if (action.type !== RECEIVED_CONSOLIDATED_PERFORMANCE) {
    return state;
  }
  return action.accountPerformanceAggregate;
};

export const productPerformance = (state = [], action) => {
  if (action.type === RECEIVED_PRODUCT_PERFORMANCE) {
    return action.productPerformance;
  }

  return state;
};

export const aggregatedProductPerformance = (state = [], action) => {
  if (action.type === RECEIVED_AGGREGATED_PRODUCT_PERFORMANCE) {
    // Returns aggregate so wrap in array
    return [
      { ...action.productPerformance, productClass: PERSONAL_INVESTMENT }
    ];
  }

  return state;
};

export const accountPerformance = (state = [], action) => {
  if (action.type !== RECEIVED_ACCOUNT_PERFORMANCE) {
    return state;
  }

  return action.accountPerformance;
};

export const accountPerformanceMap = (state = [], action) => {
  if (action.type === RECEIVED_ACCOUNT_PERFORMANCE) {
    const accountPerformanceMap = {};
    const { accountPerformance } = action;
    if (Array.isArray(accountPerformance)) {
      accountPerformance.forEach((accountWithPerformance) => {
        const { accountId } = accountWithPerformance;
        if (accountId) {
          accountPerformanceMap[accountId] = accountWithPerformance;
        }
      });
    }
    return accountPerformanceMap;
  }
  return state;
};
accountPerformanceMap.priority = 17;

const accountPerformanceTableRecalcTriggers = [
  RECEIVED_ACCOUNT_PERFORMANCE,
  RECEIVED_CONSOLIDATED_PERFORMANCE,
  RECEIVED_ACCOUNTS,
  SELECTED_ACCOUNT_FILTER,
  TOGGLED_ALL_PURPOSES,
  TOGGLED_PRODUCT,
  TOGGLED_ALL_PRODUCTS
].reduce((triggers, trigger) => {
  triggers[trigger] = true;
  return triggers;
}, {});

export const accountPerformanceTable = (state = [], action, fullState) => {
  if (!accountPerformanceTableRecalcTriggers[action.type]) {
    return state;
  }

  const { accountPerformanceAggregate, accountPerformance, selections } =
    fullState;

  if (
    selections == null ||
    isEmpty(selections.accountIds) ||
    accountPerformanceAggregate == null ||
    accountPerformance == null
  ) {
    return state;
  }

  const accountsById = selections.accountIds;

  const table = accountPerformance
    .map((account) => {
      const info = accountsById[account.accountId];
      if (info === undefined) {
        return null;
      }
      return {
        ...info,
        ...account,
        label: accountName(info),
        number: formatAccountNumber(info),
        rowType: 'fund'
      };
    })
    .filter((a) => a !== null);

  table.sort(sortAccountsByPurposeAndProduct);

  let i = table.length - 1;
  let lastAccount = table[i];
  while (i-- >= 0) {
    let thisAccount = table[i] || {};
    const accountTypeChanged =
      thisAccount.accountType !== lastAccount.accountType;
    const purposeChanged =
      thisAccount.retirementAccount !== lastAccount.retirementAccount;

    if (accountTypeChanged || purposeChanged) {
      const accountTypeRow = {
        label: lastAccount.accountType,
        rowType: 'account'
      };

      table.splice(i + 1, 0, accountTypeRow);

      if (purposeChanged) {
        const purposeRow = {
          label: lastAccount.retirementAccount
            ? 'Retirement'
            : 'Non-Retirement',
          rowType: 'purpose'
        };
        table.splice(i + 1, 0, purposeRow);
      }
    }

    lastAccount = thisAccount;
  }

  const totalsRow = {
    ...accountPerformanceAggregate,
    accountNumber: '',
    label: 'Total Account',
    rowType: 'totals'
  };

  table.unshift(totalsRow);

  return table;

  // Given two accounts, sorts by purpose and product, then name.
  // must be inner function because needs access to state data
  function sortAccountsByPurposeAndProduct(accountX, accountY) {
    if (accountX.retirementAccount !== accountY.retirementAccount) {
      return accountX.retirementAccount ? -1 : 1;
    }

    if (accountX.accountType !== accountY.accountType) {
      if (
        !collegeAcctNames.includes(accountX.accountType) &&
        !collegeAcctNames.includes(accountY.accountType)
      ) {
        // Neither are 529 accounts
        return accountX.accountType < accountY.accountType ? -1 : 1;
      }
      if (
        collegeAcctNames.includes(accountX.accountType) &&
        collegeAcctNames.includes(accountY.accountType)
      ) {
        // Both are 529 accounts
        return collegeAcctNames.indexOf(accountX.accountType) <
          collegeAcctNames.indexOf(accountY.accountType)
          ? -1
          : 1;
      }
      if (
        collegeAcctNames.includes(accountX.accountType) &&
        !collegeAcctNames.includes(accountY.accountType)
      ) {
        // First is 529, second is not
        return 1;
      }
      if (
        !collegeAcctNames.includes(accountX.accountType) &&
        collegeAcctNames.includes(accountY.accountType)
      ) {
        // Second is 529, first is not
        return -1;
      }
    }

    return accountX.accountName === accountY.accountName
      ? 0
      : accountX.accountName < accountY.accountName
      ? -1
      : 1;
  }
};

export const history = (state = [], action) => {
  if (action.type === RECEIVED_HISTORY) {
    if (!(action.history && isArray(action.history))) {
      return state;
    }
    return action.history.map((h) => ({
      ...h,
      deductions: -Math.abs(h.deductions)
    }));
  }
  return state;
};

const collegeAcctNames = [
  'T. Rowe Price College Savings Plan',
  'Maryland Senator Edward J. Kasemeyer College Investment Plan',
  'UA College Savings Plan'
];

export const growth = (state = [], action) => {
  if (action.type === RECEIVED_ACCOUNT_GROWTH) {
    return aggregateGrowth(action.growth.history);
  }
  return state;
};

export const individualGrowth = (state = [], action) => {
  if (action.type === RECEIVED_INDIVIDUAL_ACCOUNT_GROWTH) {
    return aggregateGrowth(action.growth.history);
  }
  return state;
};

export const metrics = (state = {}, action) => {
  if (action.type === RECEIVED_ACCOUNT_GROWTH) {
    return aggregateMetrics(action.growth.history);
  }
  return state;
};

export const iraContributionYearToggle = (state, action) => {
  if (action.type === CHANGE_IRA_CONTRIBUTION_TOGGLE) {
    return action.value;
  }
  return state;
};

export const mailingAddress = (state = {}, action) => {
  if (action.type === RECEIVED_MAILING_ADDRESS) {
    return action.mailingAddress;
  }
  return state;
};

export const residentialAddress = (state = {}, action) => {
  if (action.type === RECEIVED_RESIDENTIAL_ADDRESS) {
    return action.residentialAddress;
  }
  return state;
};
